import { useState, useEffect } from 'react';
import {
  Box, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Typography, TextField, InputAdornment, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, alpha, useTheme
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Receipt as ReceiptIcon, Search as SearchIcon } from '@mui/icons-material';
import { supabase } from '../../../../supabaseClient';
import { SectionPaper, SectionTitle, LineItemRow, NumberTextField } from './styledComponents';
import { formatCurrency } from './utils';
import ProductSelector from './ProductSelector';

const LineItemSection = ({
  lineItems, invoice, priceLists, currentCompany,
  onLineItemsChange, onLineItemChange, onRemoveLineItem, onUpdate,
  companyId, canEdit = true, canDelete = true
}) => {
  const theme = useTheme();
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [priceListItems, setPriceListItems] = useState([]);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, index: null, item: null });
  const [priceListDialogOpen, setPriceListDialogOpen] = useState(false);
  const [priceListSearchTerm, setPriceListSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [massDeleteConfirmOpen, setMassDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    if (invoice?.price_list_id && companyId) fetchPriceListItems(invoice.price_list_id);
    else setPriceListItems([]);
  }, [invoice?.price_list_id, companyId]);

  const fetchPriceListItems = async (priceListId) => {
    try {
      const { data, error } = await supabase.from('price_list_items').select('*').eq('price_list_id', priceListId);
      if (error) throw error;
      setPriceListItems(data || []);
    } catch (err) { console.error('Error fetching price list items:', err); setPriceListItems([]); }
  };

  const getPriceListItem = (item) => {
    if (!item.product_id || priceListItems.length === 0) return null;
    return priceListItems.find(p => p.product_id === item.product_id && (item.variant_id ? p.variant_id === item.variant_id : !p.variant_id)) || null;
  };

  const handlePriceTierChange = (index, tier) => {
    const item = lineItems[index];
    const plItem = getPriceListItem(item);
    if (!plItem) return;
    const newPrice = tier === 'pallet'
      ? parseFloat(plItem.pallet_price || plItem.case_price || 0)
      : parseFloat(plItem.case_price || plItem.price || 0);
    const qty = parseFloat(item.quantity) || 0;
    onLineItemsChange(lineItems.map((li, i) => i === index ? { ...li, price_tier: tier, unit_price: newPrice, line_total: qty * newPrice, total_price: qty * newPrice } : li));
  };

  const handleItemsAdded = (newItems) => {
    const merged = [...lineItems];
    newItems.forEach(newItem => {
      const existingIdx = merged.findIndex(li =>
        li.product_id === newItem.product_id &&
        li.variant_id === newItem.variant_id &&
        li.unit_type === newItem.unit_type &&
        parseFloat(li.unit_price) === parseFloat(newItem.unit_price)
      );
      if (existingIdx >= 0) {
        const existing = merged[existingIdx];
        const qty = (parseFloat(existing.quantity) || 0) + (parseFloat(newItem.quantity) || 0);
        const price = parseFloat(existing.unit_price) || 0;
        merged[existingIdx] = { ...existing, quantity: qty, line_total: qty * price, total_price: qty * price };
      } else {
        merged.push(newItem);
      }
    });
    onLineItemsChange(merged);
  };

  const handleRemoveLineItem = (index) => {
    setDeleteConfirmDialog({ open: true, index, item: lineItems[index] });
  };

  const handleConfirmDelete = () => {
    const { index } = deleteConfirmDialog;
    onRemoveLineItem(index);
    setSelectedRows(prev => { const n = new Set(); prev.forEach(i => { if (i < index) n.add(i); else if (i > index) n.add(i - 1); }); return n; });
    setDeleteConfirmDialog({ open: false, index: null, item: null });
  };

  const handleDeleteSelected = () => {
    onLineItemsChange(lineItems.filter((_, i) => !selectedRows.has(i)));
    setSelectedRows(new Set());
  };

  const getFilteredPriceLists = () => {
    if (!priceLists) return [];
    if (!priceListSearchTerm.trim()) return priceLists;
    const term = priceListSearchTerm.toLowerCase();
    return priceLists.filter(l => l.name.toLowerCase().includes(term) || l.type.toLowerCase().includes(term));
  };

  const handlePriceListSelect = (priceListId) => {
    const selectedList = priceLists?.find(pl => pl.id === priceListId);
    onUpdate({ price_list_id: priceListId, price_list_name: selectedList ? `${selectedList.name} (${selectedList.type})` : '' });
    setPriceListDialogOpen(false);
    setPriceListSearchTerm('');
  };

  return (
    <>
      <SectionPaper sx={{ width: '100%', mt: 3 }}>
        <SectionTitle variant="h6"><ReceiptIcon />Line Items</SectionTitle>

        {/* Price List Selector */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>Price List</Typography>
          <Button fullWidth variant="outlined"
            onClick={() => canEdit && setPriceListDialogOpen(true)}
            sx={{
              borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, background: 'white',
              minHeight: 56, justifyContent: 'flex-start', px: 3, py: 2.5,
              opacity: canEdit ? 1 : 0.3, pointerEvents: canEdit ? 'auto' : 'none',
              '&:hover': { border: `1px solid ${theme.palette.primary.main}`, boxShadow: canEdit ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}` : 'none' }
            }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              {invoice.price_list_id ? (
                <>
                  <Typography variant="body1" fontWeight={500}>
                    {invoice.price_list_name || (() => { const l = priceLists?.find(pl => pl.id === invoice.price_list_id); return l ? `${l.name} (${l.type})` : 'Price List Selected'; })()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{currentCompany?.name || 'Your Company'}</Typography>
                </>
              ) : (
                <>
                  <Typography variant="body1" fontWeight={500}>Standard Retail Prices</Typography>
                  <Typography variant="caption" color="text.secondary">{currentCompany?.name || 'Your Company'} - Default Product Pricing</Typography>
                </>
              )}
            </Box>
          </Button>
        </Box>

        {/* Line Items Table */}
        <TableContainer sx={{
          overflowX: 'auto', borderRadius: 3, backgroundColor: '#ffffff',
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.12)}`
        }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), '& .MuiTableCell-root': { borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, fontWeight: 500, fontSize: '0.8rem', color: 'primary.main' } }}>
                <TableCell sx={{ width: 140 }}>Product</TableCell>
                <TableCell align="center" sx={{ width: 120 }}>Size</TableCell>
                <TableCell align="center" sx={{ width: 80 }}>UoM</TableCell>
                <TableCell align="center" sx={{ width: 100 }}>Qty</TableCell>
                <TableCell align="center" sx={{ width: 130 }}>Unit Price</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>Amount</TableCell>
                <TableCell width={50} align="center" />
              </TableRow>
            </TableHead>
            <TableBody>
              {lineItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                    <Typography color="text.secondary" variant="body2">No line items yet. Click "Add Line Item" to get started.</Typography>
                  </TableCell>
                </TableRow>
              ) : lineItems.map((item, index) => (
                <LineItemRow key={item.id || index}>
                  {/* Product */}
                  <TableCell>
                    {item.product_id ? (
                      <Typography variant="body2" fontWeight={500} sx={{ px: 1 }}>{item.product_name || 'Unknown Product'}</Typography>
                    ) : null}
                  </TableCell>

                  {/* Size / Variant */}
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: item.variant_name ? 'text.primary' : 'text.secondary' }}>
                      {item.variant_name || '-'}
                    </Typography>
                  </TableCell>

                  {/* UoM */}
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      {item.product_id && item.unit_type !== 'Unit' && item.units_per_case ? `${item.units_per_case}U/C` : '-'}
                    </Typography>
                  </TableCell>

                  {/* Qty */}
                  <TableCell align="center">
                    {item.product_id ? (
                      <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                        <NumberTextField
                          type="number"
                          value={item.quantity ?? ''}
                          onChange={(e) => { if (canEdit) { const val = e.target.value; onLineItemChange(index, 'quantity', val === '' ? '' : val); } }}
                          onBlur={(e) => { if (canEdit) { const val = parseFloat(e.target.value); if (isNaN(val) || val < 0) onLineItemChange(index, 'quantity', '0'); } }}
                          inputProps={{ min: 0, step: 1 }}
                          size="small" disabled={!canEdit}
                          sx={{ width: '80px', opacity: canEdit ? 1 : 0.6 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {item.unit_type === 'Unit' ? (parseFloat(item.quantity) === 1 ? 'Unit' : 'Units') : (parseFloat(item.quantity) === 1 ? 'Case' : 'Cases')}
                        </Typography>
                      </Box>
                    ) : '-'}
                  </TableCell>

                  {/* Price */}
                  <TableCell align="center">
                    {(() => {
                      const plItem = getPriceListItem(item);
                      const hasPallet = plItem && parseFloat(plItem.pallet_price) > 0;
                      const tier = item.price_tier || 'case';
                      return (
                        <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.4 }}>
                          <NumberTextField
                            type="number"
                            value={item.unit_price === 0 || item.unit_price === '' || item.unit_price === null || item.unit_price === undefined ? '' : item.unit_price}
                            onChange={(e) => { if (canEdit) { const val = e.target.value; onLineItemChange(index, 'unit_price', val === '' ? '' : val); } }}
                            onBlur={(e) => { if (canEdit) { const val = parseFloat(e.target.value); if (isNaN(val) || val < 0) onLineItemChange(index, 'unit_price', '0'); } }}
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            size="small" disabled={!canEdit}
                            sx={{ width: '110px', opacity: canEdit ? 1 : 0.6 }}
                          />
                          {hasPallet && canEdit && (
                            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                              <Typography variant="caption" onClick={() => handlePriceTierChange(index, 'case')}
                                sx={{ cursor: 'pointer', color: tier === 'case' ? 'primary.main' : 'text.disabled', '&:hover': { color: 'primary.main' } }}>Case</Typography>
                              <Typography variant="caption" sx={{ color: 'text.disabled' }}>·</Typography>
                              <Typography variant="caption" onClick={() => handlePriceTierChange(index, 'pallet')}
                                sx={{ cursor: 'pointer', color: tier === 'pallet' ? 'primary.main' : 'text.disabled', '&:hover': { color: 'primary.main' } }}>Pallet</Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })()}
                  </TableCell>

                  {/* Amount */}
                  <TableCell align="right">
                    <Tooltip title={formatCurrency(item.line_total || item.total_price)} arrow placement="left">
                      <Typography sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px', fontWeight: 500, color: 'success.main', fontSize: '0.9rem' }}>
                        {formatCurrency(item.line_total || item.total_price)}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  {/* Delete */}
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => canDelete && handleRemoveLineItem(index)}
                      sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`, color: 'white', opacity: canDelete ? 1 : 0.3, pointerEvents: canDelete ? 'auto' : 'none', '&:hover': { transform: canDelete ? 'scale(1.1)' : 'none', border: `1px solid ${theme.palette.error.main}` } }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </LineItemRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button startIcon={<AddIcon />} onClick={canEdit ? () => setShowProductDialog(true) : undefined} variant="outlined"
            sx={{ borderRadius: 2, fontWeight: 500, px: 3, py: 1.5, background: 'white', border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, opacity: canEdit ? 1 : 0.3, pointerEvents: canEdit ? 'auto' : 'none', '&:hover': { transform: canEdit ? 'translateY(-1px)' : 'none' } }}>
            Add Line Item
          </Button>
          {selectedRows.size > 0 && canDelete && (
            <Button startIcon={<DeleteIcon />} onClick={() => setMassDeleteConfirmOpen(true)} variant="outlined" color="error"
              sx={{ borderRadius: 2, fontWeight: 500, px: 3, py: 1.5, background: 'white' }}>
              Delete Selected ({selectedRows.size})
            </Button>
          )}
        </Box>
      </SectionPaper>

      <ProductSelector
        open={showProductDialog}
        onClose={() => setShowProductDialog(false)}
        onItemsAdded={handleItemsAdded}
        companyId={companyId}
        invoice={invoice}
        existingLineItems={lineItems}
      />

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmDialog.open} onClose={() => setDeleteConfirmDialog({ open: false, index: null, item: null })} maxWidth="sm" fullWidth disableScrollLock
        PaperProps={{ sx: { borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` } }}>
        <DialogTitle sx={{ borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`, fontWeight: 500, color: 'error.main' }}>Confirm Delete</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 2, mt: 2 }}>Are you sure you want to remove this line item?</Typography>
          {deleteConfirmDialog.item && (
            <Box sx={{ p: 2, bgcolor: alpha(theme.palette.grey[100], 0.5), borderRadius: 2, mb: 2 }}>
              <Typography variant="body2" fontWeight={500}>{deleteConfirmDialog.item.product_name || 'Item'}</Typography>
              {deleteConfirmDialog.item.variant_name && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Variant: {deleteConfirmDialog.item.variant_name}</Typography>}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
          <Button onClick={() => setDeleteConfirmDialog({ open: false, index: null, item: null })} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}
            sx={{ borderRadius: 2, background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})` }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Mass Delete Confirmation */}
      <Dialog open={massDeleteConfirmOpen} onClose={() => setMassDeleteConfirmOpen(false)} maxWidth="xs" fullWidth disableScrollLock>
        <DialogTitle sx={{ fontWeight: 500, color: 'error.main' }}>Delete {selectedRows.size} Line Item{selectedRows.size > 1 ? 's' : ''}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">This will permanently remove the selected line item{selectedRows.size > 1 ? 's' : ''} from the invoice.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setMassDeleteConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => { handleDeleteSelected(); setMassDeleteConfirmOpen(false); }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Price List Dialog */}
      <Dialog open={priceListDialogOpen} onClose={() => setPriceListDialogOpen(false)} maxWidth="md" fullWidth disableScrollLock>
        <DialogTitle sx={{ fontWeight: 500, color: 'primary.main' }}>Select Price List</DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <TextField fullWidth placeholder="Search price lists..." value={priceListSearchTerm}
            onChange={(e) => setPriceListSearchTerm(e.target.value)} sx={{ mb: 3 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ p: 3, borderRadius: 2, border: !invoice.price_list_id ? `2px solid ${theme.palette.primary.main}` : `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, bgcolor: !invoice.price_list_id ? alpha(theme.palette.primary.main, 0.08) : 'white', cursor: 'pointer' }}
                onClick={() => handlePriceListSelect('')}>
                <Typography variant="body1" fontWeight={500}>Standard Retail Prices</Typography>
                <Typography variant="caption" color="text.secondary">Default Product Pricing</Typography>
              </Box>
            </Grid>
            {getFilteredPriceLists().map((list) => (
              <Grid item xs={12} key={list.id}>
                <Box sx={{ p: 3, borderRadius: 2, border: invoice.price_list_id === list.id ? `2px solid ${theme.palette.primary.main}` : `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, bgcolor: invoice.price_list_id === list.id ? alpha(theme.palette.primary.main, 0.08) : 'white', cursor: 'pointer' }}
                  onClick={() => handlePriceListSelect(list.id)}>
                  <Typography variant="body1" fontWeight={500}>{list.name} ({list.type})</Typography>
                  <Typography variant="caption" color="text.secondary">{currentCompany?.name}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setPriceListDialogOpen(false)}>Close</Button>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => window.open(`/company/${companyId}/price-lists`, '_blank')}>Add New Price List</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LineItemSection;
